import React from 'react';

import { ChevronDownIcon } from '../../assets';
import TrackBar from './TrackBar';

/**
 * One chapter, open or closed — the card the "Continue studying" and
 * "Performance" frames both stack (16370:189829, 16370:189866, 16370:190357).
 *
 * The header is the whole of a closed card: the title and its chevron at the
 * inline start, the chapter's bar and percentage at the end. Opening it rules a
 * gray/300 line under the header and shows whatever the tab puts in `children`
 * — the lessons on one tab, the performance table on the other.
 *
 * The two frames draw the card a little differently and both are kept:
 *  - on "فصول الكتاب" a closed card is 12px round and flat, an open one 16px;
 *  - on "الأداء" every card is 16px round and carries the card shadow (`raised`).
 *
 * The percentage is lettered by the caller (`progressText`, `progressTone`),
 * because the frames colour it by rules of their own — brand blue once
 * something is done on one tab, slate on every closed card on the other.
 *
 * ORDER
 *
 * Title before chevron, and bar before percentage: in Arabic the chevron lands
 * on the title's left and the number on the bar's left, where the design puts
 * both. The whole header is one <button> with `aria-expanded`, inside the `h3`
 * that names the chapter.
 */
const ChapterPanel = ({
    id, title, percent, progressText, progressTone = 'brand', trackTone = 'brand',
    isOpen = false, raised = false, onToggle, children,
}) => {
    const shape = isOpen || raised ? 'tw-rounded-[16px]' : 'tw-rounded-[12px]';

    return (
        <div className={`tw-overflow-hidden tw-border tw-border-solid tw-border-v2-ground-400 tw-bg-white ${shape} ${raised ? 'tw-shadow-v2-card' : ''}`}>
            <h3 className="tw-m-0">
                <button
                    type="button"
                    id={`${id}-header`}
                    aria-expanded={isOpen}
                    aria-controls={`${id}-body`}
                    onClick={onToggle}
                    className={
                        'tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-gap-[16px] ' +
                        'tw-border-0 tw-bg-white tw-p-[24px] tw-text-start focus-visible:tw-outline-none ' +
                        'focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-v2-brand ' +
                        (isOpen ? 'tw-border-b tw-border-solid tw-border-v2-ground-400' : '')
                    }
                >
                    <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-[16px]">
                        <span className="tw-min-w-0 tw-truncate tw-text-[16px] tw-font-bold tw-leading-[1.5] tw-text-v2-student-name">
                            {title}
                        </span>
                        <ChevronDownIcon
                            width={16}
                            height={16}
                            aria-hidden
                            className={`tw-shrink-0 tw-text-v2-student-nav tw-transition-transform ${isOpen ? 'tw-rotate-180' : ''}`}
                        />
                    </span>

                    <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-[12px]">
                        <TrackBar value={percent} tone={trackTone} className="tw-hidden tw-w-[120px] sm:tw-block" />
                        <span
                            className={
                                'tw-whitespace-nowrap tw-text-[14px] tw-font-bold tw-leading-[1.6] ' +
                                (progressTone === 'muted' ? 'tw-text-v2-student-nav' : 'tw-text-v2-brand')
                            }
                        >
                            {progressText}
                        </span>
                    </span>
                </button>
            </h3>

            {isOpen && (
                <div id={`${id}-body`} role="region" aria-labelledby={`${id}-header`}>
                    {children}
                </div>
            )}
        </div>
    );
};

export default ChapterPanel;
